<template>
  <div>
    <Typography variant="h-1">Sign up and get rewarded!</Typography>
    <v-form ref="form" v-model="validForm" class="full-width px-4 pt-8">
      <v-text-field
        ref="firstName"
        label="First Name*"
        v-model="localFirstName"
        outlined
        color="brandPrimary"
        :rules="[
          (v) => !!v || 'Your first name is required',
          (v) => !!(v && v.length < 40) || 'Your first name should be shorter',
        ]"
      />
      <v-text-field
        ref="lastName"
        label="Last Name*"
        v-model="localLastName"
        color="brandPrimary"
        outlined
        :rules="[
          (v) => !!v || 'Your last name is required',
          (v) => !!(v && v.length < 40) || 'Your last name should be shorter',
        ]"
      />
      <v-text-field
        ref="email"
        :label="client?.one10PointsEnabled ? 'Email*' : 'Phone or Email*'"
        outlined
        v-model="localEmail"
        color="brandPrimary"
        :rules="emailRules"
        @change="handleEmailChange"
        @input="handleEmailChange"
      />
      <v-select
        label="Country"
        :items="countries"
        v-model="localCountry"
        item-value="code"
        item-text="name"
        color="brandPrimary"
        outlined
        :rules="[(v) => !!v || 'Country is required']"
      />
      <v-text-field
        v-if="localCountry === '00'"
        v-model="localOtherCountry"
        color="brandPrimary"
        outlined
        :rules="[
          (v) => !!v || 'Country code is required',
          (v) => !!(v && v.length == 2) || 'Country code should be 2 letters',
        ]"
        label="Country (2 digit code)*"
      ></v-text-field>
      <div v-for="field in additionalFields" :key="field.userAttribute">
        <v-text-field
          v-if="!Array.isArray(field.dropdownOptions) || field.dropdownOptions.length === 0"
          :label="getLabelByUserAttribute(field.userAttribute) + '*'"
          v-model="
            additionalFieldValues.find((value) => value.userAttribute === field.userAttribute).value
          "
          outlined
          color="brandPrimary"
          :rules="[(v) => !!v || `${getLabelByUserAttribute(field.userAttribute)} is required`]"
        />
        <v-select
          v-else
          :items="field.dropdownOptions"
          :label="getLabelByUserAttribute(field.userAttribute) + '*'"
          v-model="
            additionalFieldValues.find((value) => value.userAttribute === field.userAttribute).value
          "
          outlined
          color="brandPrimary"
          :rules="[(v) => !!v || `${getLabelByUserAttribute(field.userAttribute)} is required`]"
        />
      </div>
    </v-form>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import { countries, emailRegex } from "@/shared_data/data.js";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";

export default {
  name: "SignUpForm",
  components: {
    Typography,
  },
  props: {
    client: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Boolean,
      default: false,
    },
    firstName: {
      type: String,
      default: null,
    },
    lastName: {
      type: String,
      default: null,
    },
    email: {
      type: String,
      default: null,
    },
    country: {
      type: String,
      default: null,
    },
    otherCountry: {
      type: String,
      default: null,
    },
    additionalFields: {
      type: Array,
      default: () => [],
    },
    additionalFieldValues: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      emailRegex,
      countries,
      validForm: this.value,
      validPhone: false,
      formattedPhone: null,
    };
  },
  computed: {
    localFirstName: {
      get() {
        return this.firstName;
      },
      set(val) {
        this.$emit("update:firstName", val);
        this.emitFormData();
      },
    },
    localLastName: {
      get() {
        return this.lastName;
      },
      set(val) {
        this.$emit("update:lastName", val);
        this.emitFormData();
      },
    },
    localEmail: {
      get() {
        return this.email;
      },
      set(val) {
        this.$emit("update:email", val);
        this.emitFormData();
      },
    },
    localCountry: {
      get() {
        return this.country;
      },
      set(val) {
        this.$emit("update:country", val);
        this.emitFormData();
      },
    },
    localOtherCountry: {
      get() {
        return this.otherCountry;
      },
      set(val) {
        this.$emit("update:otherCountry", val);
        this.emitFormData();
      },
    },
    emailRules() {
      if (this.client?.one10PointsEnabled) {
        // Email only validation
        return [
          (v) => !!v || "Your email is required",
          (v) => !!(v && v.length < 255) || "Your email should be shorter",
          (v) => !!(v && this.emailRegex.test(v)) || "Your email does not appear valid",
        ];
      }
      // Phone or Email validation
      return [
        (v) => !!v || "Your email or phone number is required",
        (v) => !!(v && v.length < 255) || "Your email/phone should be shorter",
        (v) =>
          !!(v && (this.emailRegex.test(v) || this.validPhone)) ||
          "Your email/phone does not appear valid",
      ];
    },
  },
  watch: {
    validForm(newVal) {
      this.$emit("input", newVal);
    },
    value(newVal) {
      this.validForm = newVal;
    },
    formattedPhone(newVal) {
      this.$emit("update:formattedPhone", newVal);
    },
  },
  methods: {
    getLabelByUserAttribute(userAttribute) {
      return userAttribute
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    },
    handleEmailChange(val) {
      // Only run phone validation if one10PointsEnabled is false
      if (!this.client?.one10PointsEnabled) {
        this.phoneValidation(val);
      }
    },
    phoneValidation(val) {
      let isValid = false;
      isValid = isValidPhoneNumber(val, "US");
      this.validPhone = isValid;
      if (isValid) {
        this.formattedPhone = parsePhoneNumber(val, "US").format("E.164");
      } else {
        this.formattedPhone = null;
      }
    },
    emitFormData() {
      this.$emit("form-data", {
        firstName: this.localFirstName,
        lastName: this.localLastName,
        email: this.localEmail,
        country: this.localCountry,
        otherCountry: this.localOtherCountry,
        formattedPhone: this.formattedPhone,
        validPhone: this.validPhone,
      });
    },
  },
  mounted() {
    console.log("Additional fields", this.additionalFields);
    console.log("Additional field values", this.additionalFieldValues);
  },
};
</script>

<style scoped></style>
